import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const THRESHOLD = 70;

/** Pull the page down from the top to reload the current lists, as in a native app. */
export const PullToRefresh = ({ onRefresh, children }) => {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const startY = useRef(null);
  const distance = useRef(0);
  const working = useRef(false);

  useEffect(() => {
    const begin = event => {
      startY.current = window.scrollY <= 2 && !working.current ? event.touches[0].clientY : null;
      distance.current = 0;
    };
    const move = event => {
      if (startY.current === null) return;
      const delta = event.touches[0].clientY - startY.current;
      distance.current = delta > 0 && window.scrollY <= 2 ? Math.min(delta * 0.5, 96) : 0;
      setPull(distance.current);
    };
    const end = async () => {
      const reached = distance.current >= THRESHOLD;
      startY.current = null;
      distance.current = 0;
      if (!reached) {
        setPull(0);
        return;
      }
      working.current = true;
      setBusy(true);
      setPull(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        working.current = false;
        setBusy(false);
        setPull(0);
      }
    };
    window.addEventListener('touchstart', begin, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', end);
    return () => {
      window.removeEventListener('touchstart', begin);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', end);
    };
  }, [onRefresh]);

  return (
    <div data-testid="pull-to-refresh">
      <div className="pointer-events-none flex items-center justify-center overflow-hidden"
        style={{ height: pull, transition: pull ? 'none' : 'height 220ms ease-out' }}>
        <RefreshCw data-testid="pull-to-refresh-spinner"
          className={`h-5 w-5 text-[#0F4C81] ${busy ? 'animate-spin' : ''}`}
          style={{ opacity: Math.min(pull / THRESHOLD, 1), transform: `rotate(${pull * 3}deg)` }} />
      </div>
      {children}
    </div>
  );
};
